import { useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Form,
  Label,
  Input,
} from "reactstrap";
import classnames from "classnames";
import CountUp from "react-countup";
import moment from "moment";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import { withTranslation } from "react-i18next";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { useFormik } from "formik";
import "swiper/css/free-mode";
import "swiper/css/thumbs";
import SwiperCore, { FreeMode, Navigation, Thumbs } from "swiper";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import * as Yup from "yup";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import Loader from "../../Components/Common/Loader";
import {
  isPendingExpertRequest,
  useApproveExpert,
  useCertifyExpert,
  useExpert,
  useRecommendExpert,
  useRejectExpert,
  useUnvalidateExpert,
  useValidateExpert,
} from "../../hooks/useExperts";
import { useUsersList } from "../../hooks/useUsersList";
import {
  getStoredAuthUser,
  normalizeBackendFileUrl,
} from "../../utils/backendApi";
import { VALIDATION_REPORT_MAX_BYTES } from "../../constants/validationReport";
SwiperCore.use([FreeMode, Navigation, Thumbs]);

/** "DD/MM/YYYY HH:mm", or null when the API sent nothing usable. */
const formatRequestDate = (iso) => {
  if (!iso) return null;
  const parsed = moment(iso);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : null;
};

function ExpertsDetails(props) {
  const { id } = useParams();
  const history = useHistory();

  const tokenInfo = useMemo(() => {
    const stored = getStoredAuthUser()?.role;
    return Array.isArray(stored) ? stored : [];
  }, []);

  const expertQ = useExpert(id);
  const usersListQ = useUsersList();

  const selectAticle = expertQ.data || {};
  // Both arrays are returned for admin/committee viewers only, and not at all
  // by a backend deployed before they existed: default to empty either way.
  const competences = Array.isArray(selectAticle?.competences)
    ? selectAticle.competences
    : [];
  const references = Array.isArray(selectAticle?.references)
    ? selectAticle.references
    : [];
  const isPendingRequest = isPendingExpertRequest(selectAticle);
  const requestedAt = formatRequestDate(selectAticle?.expert_requested_at);

  const usersListRaw = usersListQ.data || [];
  const userList = Array.isArray(usersListRaw)
    ? usersListRaw
    : usersListRaw?.results ?? [];

  const findUserById = (userId) =>
    userList?.find((u) => String(u?.id) === String(userId));

  const users = (uOrId) => {
    if (uOrId && typeof uOrId === "object") {
      const p = uOrId?.profile;
      const fullName = `${p?.last_name || uOrId?.last_name || ""} ${
        p?.first_name || uOrId?.first_name || ""
      }`.trim();
      return fullName || uOrId?.username || uOrId?.email || "-";
    }
    const u = findUserById(uOrId);
    const p = u?.profile;
    const fullName = `${p?.last_name || ""} ${p?.first_name || ""}`.trim();
    return fullName || p?.username || u?.email || "-";
  };

  const username = (uOrId) => {
    if (uOrId && typeof uOrId === "object") {
      return uOrId?.username || "-";
    }
    const u = findUserById(uOrId);
    return u?.profile?.username || u?.username || "-";
  };

  const email = (uOrId) => {
    if (uOrId && typeof uOrId === "object") {
      return uOrId?.email || "-";
    }
    const u = findUserById(uOrId);
    return u?.email || "-";
  };

  const photo = (uOrId) => {
    if (uOrId && typeof uOrId === "object") {
      return uOrId?.profile_pic || uOrId?.profile?.profile_pic || "";
    }
    const u = findUserById(uOrId);
    return u?.profile?.profile_pic || "";
  };

  const validateExpert = useValidateExpert();
  const unvalidateExpert = useUnvalidateExpert();
  const approveExpert = useApproveExpert();
  const rejectExpert = useRejectExpert();
  const recommendExpert = useRecommendExpert();
  const certifyExpert = useCertifyExpert();

  // Optional PDF attached to the validation report (5 MB max, PDF only).
  const [reportFile, setReportFile] = useState(null);
  const [reportFileError, setReportFileError] = useState("");

  const handleReportFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setReportFileError("");

    if (!file) {
      setReportFile(null);
      return;
    }
    if (file.type !== "application/pdf") {
      setReportFileError("Le fichier doit être au format PDF.");
      setReportFile(null);
      event.target.value = "";
      return;
    }
    if (file.size > VALIDATION_REPORT_MAX_BYTES) {
      setReportFileError("Le fichier ne doit pas dépasser 5 Mo.");
      setReportFile(null);
      event.target.value = "";
      return;
    }
    setReportFile(file);
  };

  const [customActiveTab, setcustomActiveTab] = useState("1");
  const toggleCustom = (tab) => {
    if (customActiveTab !== tab) {
      setcustomActiveTab(tab);
    }
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      is_validated: false,
      validation_report: "",
    },

    validationSchema: Yup.object({
      // The written report is required unless a PDF is attached in its place.
      validation_report: Yup.string().when([], {
        is: () => !reportFile,
        then: Yup.string().required(
          "Renseignez le rapport ou joignez un fichier PDF"
        ),
        otherwise: Yup.string().notRequired(),
      }),
    }),

    onSubmit: (values) => {
      // Sent as FormData so the optional PDF rides along with the report.
      const validatedObject = new FormData();
      validatedObject.append("is_validated", String(values.is_validated));
      validatedObject.append("validation_report", values.validation_report || "");
      if (reportFile) {
        validatedObject.append("validation_report_file", reportFile);
      }
      const run = async () => {
        try {
          if (values.is_validated === false) {
            await unvalidateExpert.mutateAsync({
              id,
              payload: validatedObject,
            });
          } else {
            await validateExpert.mutateAsync({ id, payload: validatedObject });
          }
          validation.resetForm();
          history.push("/experts");
        } catch (_e) {
          // handled by hook
        }
      };
      run();
    },
  });
  const validation2 = useFormik({
    enableReinitialize: true,
    initialValues: {
      is_approved: false,
      is_expert: false,
      message: "",
      recommandations: "",
    },

    validationSchema: Yup.object({
      message: Yup.string().required("Ce champ est requis"),
    }),

    onSubmit: (values) => {
      const validatedObject = {
        id: selectAticle.id,
        is_approved: values.is_approved,
        is_expert: true,
        is_validated: true,
        validation_report: selectAticle.validation_report,
        message: values.message,
        recommandations: null,
      };
      const run = async () => {
        try {
          if (values.is_approved === false) {
            await rejectExpert.mutateAsync({ id, payload: validatedObject });
          } else {
            await approveExpert.mutateAsync({ id, payload: validatedObject });
          }
          validation2.resetForm();
          history.push("/experts");
        } catch (_e) {
          // handled by hook
        }
      };
      run();
    },
  });
  const validation3 = useFormik({
    enableReinitialize: true,
    initialValues: {
      is_approved: false,
      is_expert: false,
      is_validated: false,
      message: "",
      recommandations: "",
    },

    validationSchema: Yup.object({
      recommandations: Yup.string().required("Ce champ est requis"),
    }),

    onSubmit: (values) => {
      // The backend's /recommand/ reads `message`; sending `recommandations`
      // stored nothing.
      const validatedObject = {
        message: values.recommandations,
      };
      const run = async () => {
        try {
          await recommendExpert.mutateAsync({ id, payload: validatedObject });
          validation3.resetForm();
          history.push("/experts");
        } catch (_e) {
          // handled by hook
        }
      };
      run();
    },
  });

  const validation4 = useFormik({
    enableReinitialize: true,
    initialValues: {
      banca_certificate: "",
    },

    validationSchema: Yup.object({
      banca_certificate: Yup.number().required("Ce champ est requis"),
    }),

    onSubmit: (values) => {
      const validatedObject = {
        banca_certificate: Number(values.banca_certificate),
      };
      const run = async () => {
        try {
          await certifyExpert.mutateAsync({
            id: selectAticle?.id,
            payload: validatedObject,
          });
          validation4.resetForm();
          history.push("/experts");
        } catch (_e) {
          // handled by hook
        }
      };
      run();
    },
  });

  if (expertQ.isLoading) return <Loader />;
  if (expertQ.isError)
    return <Loader error={expertQ.error?.message || props.t("Erreur")} />;
  document.title = props.t("Détail d'un expert");
  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={props.t("Détail d'un expert")}
          pageTitle={props.t("Experts")}
        />
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <Row>
                  <Col lg={12}>
                    <Row className="gx-lg-5">
                      <Col xl={12}>
                        <div className="mt-xl-0 mt-5">
                          <div>
                            <div className="d-flex">
                              <img
                                src={photo(selectAticle.user)}
                                width="100"
                                style={{
                                  borderRadius: "50%",
                                  height: "100px",
                                  width: "100px",
                                  objectFit: "cover",
                                  objectPosition: "center center",
                                  paddingBottom: "10px",
                                }}
                                alt=""
                              />
                              <div
                                style={{
                                  marginLeft: "20px",
                                  marginTop: "8px",
                                }}
                              >
                                <h2
                                  style={{
                                    fontWeight: "bold",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {users(selectAticle.user)}{" "}
                                </h2>

                                <span className="text-xl fw-medium">
                                  <p>{selectAticle.profession}</p>
                                  <p className="text-muted">
                                    {selectAticle.last_certificate}
                                  </p>
                                </span>
                                {/* Application from the mobile app awaiting a
                                    decision; the date comes from the API's
                                    expert_requested_at when it sends one. */}
                                {isPendingRequest && (
                                  <span className="badge badge-soft-warning fs-12">
                                    {requestedAt
                                      ? props.t("Candidature déposée le {{date}}", {
                                          date: requestedAt,
                                        })
                                      : props.t("Date de demande inconnue")}
                                  </span>
                                )}
                              </div>

                              {selectAticle.is_certified && (
                                  <div className="flex aligns-items-center justify-items">
                                    <span className="badge badge-soft-primary me-1">
                                      {props.t("Certifié")}{" "}
                                    </span>
                                  </div>
                                )}
                            </div>
                          </div>

                          <div className="mt-2">
                            {competences.length > 0 ? (
                              competences.map((item, key) => (
                                <span
                                  key={item?.id ?? key}
                                  className="text-xl fw-medium badge badge-soft-warning me-1"
                                >
                                  {item?.label}
                                </span>
                              ))
                            ) : (
                              <span className="text-muted fs-13">
                                {props.t("(Aucune compétence déclarée)")}
                              </span>
                            )}
                          </div>

                          {/* The expert's record: how many requesters they saw
                              through, and how satisfied those requesters were.
                              This is what the platform asked for in order to
                              recognise the experts who do the work. */}
                          <Row className="mt-4 g-0 text-center border-top border-bottom py-3">
                            <Col xs={6} className="border-end">
                              <p className="text-muted text-uppercase fw-medium fs-11 mb-1">
                                {props.t("Demandeurs accompagnés")}
                              </p>
                              <h4 className="mb-0 ff-secondary fw-semibold">
                                <CountUp
                                  start={0}
                                  end={selectAticle.questions_handled || 0}
                                  duration={1}
                                  separator=" "
                                />
                              </h4>
                            </Col>
                            <Col xs={6}>
                              <p className="text-muted text-uppercase fw-medium fs-11 mb-1">
                                {props.t("Taux de satisfaction")}
                              </p>
                              {selectAticle.satisfaction_rate === null ||
                              selectAticle.satisfaction_rate === undefined ? (
                                // Never shown as 0 %: an expert nobody has
                                // judged is unrated, not bad.
                                <h4 className="mb-0 text-muted fs-15">
                                  {props.t("Aucune évaluation")}
                                </h4>
                              ) : (
                                <h4
                                  className={`mb-0 ff-secondary fw-semibold text-${
                                    selectAticle.satisfaction_rate >= 80
                                      ? "success"
                                      : selectAticle.satisfaction_rate >= 50
                                        ? "warning"
                                        : "danger"
                                  }`}
                                >
                                  <CountUp
                                    start={0}
                                    end={selectAticle.satisfaction_rate}
                                    duration={1}
                                    suffix=" %"
                                  />
                                </h4>
                              )}
                            </Col>
                          </Row>

                          <div className="product-content mt-3">
                            <Nav tabs className="nav-tabs-custom nav-success">
                              <NavItem>
                                <NavLink
                                  style={{ cursor: "pointer" }}
                                  className={classnames({
                                    active: customActiveTab === "1",
                                  })}
                                  onClick={() => {
                                    toggleCustom("1");
                                  }}
                                >
                                  {props.t("Détails")}
                                </NavLink>
                              </NavItem>
                            </Nav>

                            <TabContent
                              activeTab={customActiveTab}
                              className=""
                              id="nav-tabContent"
                            >
                              <TabPane id="nav-speci" tabId="1">
                                <div className="table-responsive">
                                  <table className="table mb-0">
                                    <tbody>
                                      <tr>
                                        <th scope="row">
                                          {" "}
                                          {props.t("Curriculum Vitae")}
                                        </th>
                                        <td>
                                          {selectAticle.cv ? (
                                            <a
                                              href={normalizeBackendFileUrl(
                                                selectAticle.cv
                                              )}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="btn btn-soft-primary btn-sm"
                                            >
                                              <i className="ri-file-text-line align-bottom me-1"></i>
                                              {props.t("Voir le CV")}
                                            </a>
                                          ) : (
                                            props.t("(Non Renseigné)")
                                          )}
                                        </td>
                                      </tr>
                                      <tr>
                                        <th scope="row">
                                          {" "}
                                          {props.t("Références")}{" "}
                                        </th>
                                        {/* One cell with a list: a <div> straight
                                            inside <tr> with <td>s inside it was
                                            invalid table markup. */}
                                        <td>
                                          {references.length > 0 ? (
                                            <ul className="list-unstyled mb-0">
                                              {references.map((item, key) => (
                                                <li
                                                  key={item?.id ?? key}
                                                  className="mb-2"
                                                >
                                                  <div className="fw-medium">
                                                    {item?.name || "-"}
                                                  </div>
                                                  {item?.email ? (
                                                    <div className="text-muted fs-13">
                                                      {item.email}
                                                    </div>
                                                  ) : null}
                                                  {item?.phone_number ? (
                                                    <div className="text-muted fs-13">
                                                      {item.phone_number}
                                                    </div>
                                                  ) : null}
                                                  {item?.address ? (
                                                    <div className="text-muted fs-13">
                                                      {item.address}
                                                    </div>
                                                  ) : null}
                                                </li>
                                              ))}
                                            </ul>
                                          ) : (
                                            props.t("(Non Renseigné)")
                                          )}
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </TabPane>
                            </TabContent>
                          </div>
                        </div>
                      </Col>
                    </Row>
                    <br></br>
                    <br></br>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
        {tokenInfo.some((role) => role === "admin") &&
          !selectAticle.is_certified && (
            <Form
              encType="multipart/form-data"
              className=" bg-white rounded p-4 my-3"
              onSubmit={(e) => {
                e.preventDefault();
                validation4.handleSubmit();
              }}
            >
              <br></br>
              <Label htmlFor="" className="form-label">
                {props.t("CERTIFICATION DE BANCA")}
              </Label>
              <Input
                className="form-control"
                name="banca_certificate"
                type="select"
                value={validation4.values.banca_certificate}
                onBlur={validation4.handleBlur}
                onChange={validation4.handleChange}
                required
              >
                <option value="">{props.t("Sélectionner")}</option>
                {[1, 2, 3, 4, 5].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </Input>

              <div className="text-end my-3">
                <button type="submit" className="btn btn-warning mx-3">
                  {props.t("Valider")}
                </button>
              </div>
            </Form>
          )}
        {!selectAticle.recommandations && (
          <Form
            encType="multipart/form-data"
            className=" bg-white rounded p-4"
            onSubmit={(e) => {
              e.preventDefault();
              validation3.handleSubmit();
            }}
          >
            <Label htmlFor="" className="form-label">
              {props.t("RECOMMANDATIONS")}
            </Label>
            <textarea
              className="form-control"
              name="recommandations"
              value={validation3.values.recommandations}
              onBlur={validation3.handleBlur}
              onChange={validation3.handleChange}
              rows="5"
              cols="20"
              required
            />

            <div className="text-end mb-3">
              {tokenInfo.some((role) => role === "admin") ? null : (
                <Link to="/experts" className="btn btn-primary">
                  {props.t("Retour")}
                </Link>
              )}

              <button type="submit" className="btn btn-warning m-3">
                {props.t("Envoyer une recommandation")}
              </button>
            </div>
          </Form>
        )}
        {/* Anyone not yet validated can be validated. An earlier written
            report — a previous non-validation — used to hide this form for
            good; it is now shown read-only above it for context. */}
        {!selectAticle.is_validated && (
          <Form
            encType="multipart/form-data"
            className=" bg-white rounded p-4"
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
            }}
          >
            {selectAticle.validation_report ? (
              <div className="alert alert-warning">
                <strong>{props.t("RAPPORT DE VALIDATION")}</strong>
                <div className="mt-1" style={{ whiteSpace: "pre-wrap" }}>
                  {selectAticle.validation_report}
                </div>
              </div>
            ) : null}
            <Label htmlFor="" className="form-label">
              {props.t("RAPPORT DE VALIDATION")}
            </Label>
            <textarea
              className="form-control"
              name="validation_report"
              value={validation.values.validation_report}
              onBlur={validation.handleBlur}
              onChange={validation.handleChange}
              rows="5"
              cols="20"
            />
            {validation.errors.validation_report &&
            validation.touched.validation_report ? (
              <div className="text-danger fs-12 mt-1">
                {validation.errors.validation_report}
              </div>
            ) : null}

            <div className="mt-3">
              <Label htmlFor="validation_report_file" className="form-label">
                {props.t("PIÈCE JOINTE")}{" "}
                <span style={{ color: "red" }}>
                  (PDF uniquement — 5 Mo maximum)
                </span>
              </Label>
              <input
                id="validation_report_file"
                type="file"
                className="form-control"
                accept="application/pdf,.pdf"
                onChange={handleReportFileChange}
              />
              {reportFileError ? (
                <div className="text-danger fs-12 mt-1">{reportFileError}</div>
              ) : (
                <small className="text-muted">
                  {reportFile
                    ? `Fichier sélectionné : ${reportFile.name}`
                    : "Facultatif si le rapport écrit est renseigné."}
                </small>
              )}
            </div>

            <br></br>
            <input
              type="checkbox"
              name="is_validated"
              className="form"
              style={{
                fontWeight: "30px",
                width: "20px",
                height: "20px",
                marginRight: "5px",
              }}
              value={validation.values.is_validated}
              onChange={validation.handleChange}
            />

            <b> Je valide cet expert </b>

            <div className="text-end mb-3">
              {tokenInfo.some((role) => role === "admin") ? null : (
                <Link to="/experts" className="btn btn-primary">
                  {props.t("Retour")}
                </Link>
              )}

              <button type="submit" className="btn btn-warning mx-3">
                {props.t("Valider")}
              </button>
            </div>
          </Form>
        )}
        {/* Validated but neither approved nor already an expert: an earlier
            rejection message no longer locks the admin out of approving. */}
        {tokenInfo.some((role) => role === "admin") &&
          selectAticle.is_validated &&
          !selectAticle.is_approved &&
          !selectAticle.is_expert && (
            <Form
              encType="multipart/form-data"
              className=" bg-white rounded p-4 my-3"
              onSubmit={(e) => {
                e.preventDefault();
                validation2.handleSubmit();
              }}
            >
              <br></br>
              <Label htmlFor="" className="form-label">
                {props.t("MOTIF")}
              </Label>
              <textarea
                className="form-control"
                name="message"
                value={validation2.values.message}
                onBlur={validation2.handleBlur}
                onChange={validation2.handleChange}
                rows="5"
                cols="20"
                required={true}
              />

              <br></br>

              <input
                type="checkbox"
                name="is_approved"
                className="form"
                style={{
                  fontWeight: "30px",
                  width: "20px",
                  height: "20px",
                  marginRight: "5px",
                }}
                value={validation2.values.is_approved}
                onChange={validation2.handleChange}
              />

              <b> J'approuve cet expert </b>

              <br></br>
              <br></br>

              <div className="text-end mb-3">
                <Link to="/experts" className="btn btn-primary">
                  {props.t("Retour")}
                </Link>
                <button type="submit" className="btn btn-warning mx-3">
                  {props.t("Valider")}
                </button>
              </div>
            </Form>
          )}
        <br></br>
        <Row>
          {(selectAticle.validation_report ||
            selectAticle.validation_report_file) && (
            <Col lg={6}>
              <Card className="p-2 h-screen">
                <Card className="h-screen rounded p-2">
                  <Label style={{ fontWeight: "bold" }}>
                    {props.t("VALIDATION EFFECTUEE PAR ")}
                  </Label>
                </Card>
                {selectAticle && selectAticle.validated_by && (
                  <div className="pb-2">
                    <div>{users(selectAticle.validated_by)}</div>
                    <div>{username(selectAticle.validated_by)}</div>
                    <div>{email(selectAticle.validated_by)}</div>
                    <br></br>
                    <div>{selectAticle.validation_report}</div>
                  </div>
                )}
                {selectAticle.validation_report_file ? (
                  <div className="pb-2">
                    <a
                      href={normalizeBackendFileUrl(
                        selectAticle.validation_report_file
                      )}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-soft-primary btn-sm"
                    >
                      <i className="ri-file-pdf-line align-bottom me-1"></i>
                      {props.t("Voir le rapport (PDF)")}
                    </a>
                  </div>
                ) : null}
              </Card>
            </Col>
          )}
          {selectAticle.is_approved && (
            <Col lg={6}>
              <Card className="p-2 h-screen">
                <Card className="h-screen rounded p-2">
                  <Label style={{ fontWeight: "bold" }}>
                    {props.t("APPROBATION EFFECTUE PAR")}
                  </Label>
                </Card>
                {selectAticle && selectAticle.validated_by && (
                  <div className="pb-2">
                    <div>{users(selectAticle.validated_by)}</div>
                    <div>{username(selectAticle.validated_by)}</div>
                    <div>{email(selectAticle.validated_by)}</div>
                    <br></br>
                    <div>{selectAticle.message}</div>
                  </div>
                )}
              </Card>
            </Col>
          )}
        </Row>
      </Container>
    </div>
  );
}

ExpertsDetails.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};
export default withRouter(withTranslation()(ExpertsDetails));
